<template>
    <v-container fluid>
        <v-row class="mt-8">
            <v-col md="2" class="hidden-sm-and-down">
                <v-banner sticky class="elevation-1" style="top: 60px">
                    <v-list dense nav>
                        <v-list-item :to="{name: 'TripsDetailItinerary', params: {id: trip.id}, hash: '#destination'}">
                            {{$t('travel.itinerary.createMap')}}
                        </v-list-item>
                        <v-list-item :to="{name: 'TripsDetailItinerary', params: {id: trip.id}, hash: '#itinerary'}">
                            {{ $t('travel.menu.tabs.itinerary') }}
                        </v-list-item>
                        <v-list-item v-for="(day, key) in trip.programs"
                                     :key="key"
                                     @click="scrollTo(`day-${key}`)">
                            {{ dayToString(day) }}
                        </v-list-item>
                    </v-list>
                </v-banner>
            </v-col>
            <v-col md="10">
                <v-card class="elevation-0 mb-6" id="destination">
                    <v-card-title class="mb-4">
                        {{ $t('travel.itinerary.createMapDescription') }}
                    </v-card-title>
                    <v-card-subtitle>
                        <v-divider></v-divider>
                    </v-card-subtitle>
                    <v-card-text>
                        <v-container fluid>
                            <v-row>
                                <v-col md="6">
                                    <v-autocomplete v-model="selectedLocation"
                                                    :items="searchResult"
                                                    :search-input.sync="mapSearch"
                                                    :loading="searchResultLoading"
                                                    item-text="label"
                                                    item-value="value">
                                    </v-autocomplete>
                                    <draggable v-model="trip.destinations">
                                        <transition-group
                                        >
                                            <div
                                                v-for="(destination, key) in trip.destinations"
                                                :key="destination.text"
                                                class="d-flex elevation-1 pa-4 mb-4"
                                                style="cursor:pointer;"
                                                @mouseover="focusDestination = key"
                                                @mouseout="focusDestination = null">
                                                <v-icon class="mr-4">mdi-drag-horizontal-variant</v-icon>
                                                <div class="d-flex align-center justify-space-between mr-2"
                                                     style="width:100%"
                                                     v-if="destinationEditIndex !== key">
                                                    <span>{{destination.text}}</span>
                                                    <v-btn icon small @click="destinationEditIndex = key">
                                                        <v-icon small>mdi-pencil</v-icon>
                                                    </v-btn>
                                                </div>
                                                <div class="d-flex align-center justify-space-between mr-2"
                                                     style="width:100%"
                                                     v-if="destinationEditIndex === key">
                                                    <v-text-field v-model="destination.text"
                                                                  label="Destination Name"
                                                                  @click:append="destinationEditIndex = -1"
                                                                  append-icon="mdi-content-save">
                                                    </v-text-field>
                                                </div>
                                                <!--suppress HtmlUnknownTarget -->
                                                <v-img
                                                        v-if="key === 0"
                                                        class="ml-auto align-self-center"
                                                        src="marker.svg"
                                                >
                                                </v-img>
                                                <!--suppress HtmlUnknownTarget -->
                                                <v-img
                                                        v-else-if="key === (trip.destinations.length - 1)"
                                                        class="ml-auto align-self-center"
                                                        src="flag.svg"
                                                >
                                                </v-img>
                                                <!--suppress HtmlUnknownTarget -->
                                                <v-img
                                                        v-else
                                                        class="ml-auto align-self-center"
                                                        src="circle.svg"
                                                >
                                                </v-img>
                                                <v-icon
                                                        @click="trip.destinations.splice(key, 1)"
                                                        class="ml-2"
                                                >
                                                    mdi-delete
                                                </v-icon>
                                            </div>
                                        </transition-group>
                                    </draggable>
                                </v-col>
                                <v-col md="6">
                                    <mgl-map :accessToken="accessToken"
                                             :mapStyle="mapStyle"
                                             @load="onMapLoaded"
                                             style="height:300px">
                                        <MglMarker v-for="(destination, key) in trip.destinations"
                                                   :key="key"
                                                   @mouseenter="focusDestination = key"
                                                   @mouseleave="focusDestination = key"
                                                   @dragend="data => setDestinationPosition(data.component, key)"
                                                   :draggable="true"
                                                   :coordinates="destination.center">
                                            <span slot="marker">
                                            <!--suppress HtmlUnknownTarget -->
                                                <v-img v-if="key === 0" class="ml-auto" src="marker.svg"></v-img>
                                                <!--suppress HtmlUnknownTarget -->
                                                <v-img v-else-if="key === (trip.destinations.length - 1)" class="ml-auto" src="flag.svg"></v-img>
                                                <!--suppress HtmlUnknownTarget -->
                                                <v-img v-else class="ml-auto" src="circle.svg"></v-img>
                                            </span>
                                        </MglMarker>
                                        <MglGeojsonLayer v-if="trip.destinations.length > 0"
                                                         sourceId="geoLocations"
                                                         :source="geoJsonSource"
                                                         layerId="path"
                                                         :layer="geoJsonLayer">
                                        </MglGeojsonLayer>
                                    </mgl-map>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-card-text>
                </v-card>
                <div id="itinerary"></div>
                <div v-for="(day, key) in trip.programs" :key="key" :ref="`day-${key}`">
                    <v-lazy min-height="500">
                    <day v-model="trip.programs[key]" :key="day.id" class="mb-8">
                        <v-menu z-index="10">
                            <template v-slot:activator="{ on, attrs }">
                                <v-select v-bind="attrs" v-on="on" :label="dayToString(day)">
                                    <template v-slot:item></template>
                                    <template v-slot:no-data>
                                        <span></span>
                                    </template>
                                </v-select>
                            </template>
                            <v-card>
                                <v-list>
                                    <v-list-item>
                                        <v-list-item-action>
                                            <div class="d-flex flex-column">
                                                <p>
                                                    {{ $t('travel.itinerary.programStepValidity') }}
                                                </p>
                                                <v-slider
                                                        v-model="amountOfDays[key]"
                                                        thumb-label="always"
                                                        class="pt-8"
                                                        :min="1"
                                                        @input="(data) => updateDays(key, data)"
                                                        :max="10">
                                                </v-slider>
                                            </div>
                                        </v-list-item-action>
                                    </v-list-item>
                                </v-list>
                            </v-card>
                        </v-menu>
                        <template v-slot:actions>
                            <div class="d-flex flex-column justify-end">
                                <v-btn
                                        fab
                                        small
                                        dark
                                        color="error"
                                        class="ml-auto mb-4"
                                        @click="deleteDay(key)"
                                >
                                    <v-icon dark>
                                        mdi-delete
                                    </v-icon>
                                </v-btn>
                                <v-btn
                                        fab
                                        small
                                        class="ml-auto mb-4"
                                        :disabled="key === 0"
                                        @click="moveDay(key, -1)"
                                >
                                    <v-icon>
                                        mdi-chevron-up
                                    </v-icon>
                                </v-btn>
                                <v-btn
                                        fab
                                        small
                                        class="ml-auto mb-4"
                                        :disabled="key === (trip.programs.length - 1)"
                                        @click="moveDay(key, 1)"
                                >
                                    <v-icon>
                                        mdi-chevron-down
                                    </v-icon>
                                </v-btn>
                            </div>
                        </template>
                    </day>
                    </v-lazy>
                </div>
                <v-dialog v-model="dialogDelete" max-width="600px">
                    <v-card>
                        <v-card-title class="headline">{{ $t('travel.downloads.delete') }}</v-card-title>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn text @click="closeDeleteDialog">{{ $t('cancel') }}</v-btn>
                            <v-btn color="secondary" @click="confirmDelete">{{ $t('ok') }}</v-btn>
                            <v-spacer></v-spacer>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
                <div class="d-flex justify-center">
                    <v-btn text outlined @click="addDay">
                        <v-icon>
                            mdi-plus
                        </v-icon>
                        {{ $t('add') }}
                    </v-btn>
                </div>
            </v-col>
        </v-row>
        <slot name="footer" :validate="validateTrip"></slot>
    </v-container>
</template>

<script>
    import {mapActions, mapGetters} from "vuex";
    import Mapbox from 'mapbox-gl';
    import {MglMap, MglMarker, MglGeojsonLayer} from 'vue-mapbox';
    import axios from 'axios';
    import draggable from 'vuedraggable';
    import {validationMixin} from 'vuelidate';
    import {SnackBarAction} from "../../../store/snackbar/types";
    import {TripAction} from "../../../store/trips/types";
    import Day from "./Itinerary/Day";

    export default {
        mixins: [
            validationMixin,
        ],
        components: {
            MglMap,
            MglMarker,
            MglGeojsonLayer,
            draggable,
            Day,
        },
        created() {
            this.mapbox = Mapbox;

            this.init();
        },
        data: () => ({
            trip: {},
            focusDestination: null,
            destinationEditIndex: -1,
            mapbox: null,
            mapSearch: null,
            selectedLocation: null,
            searchResult: [],
            searchResultLoading: false,
            accessToken: process.env.VUE_APP_MAP_BOX_API_KEY,
            mapStyle: 'mapbox://styles/asireisen/ckoyb82322crw18oa98810i4d',
            geoJsonLayer: {
                id: 'path',
                type: 'line',
                source: 'geoLocations',
                paint: {
                    'line-color': '#24374b',
                    'line-width': 5,
                }
            },
            amountOfDays: [],
            dialogDelete: false,
            deleteIndex: -1,
            defaultAccommodationData: {
                name: '',
                street: '',
                zip: '',
                city: '',
                country: '',
            },
            defaultDay: {
                dayFrom: null,
                toTo: null,
                title: '',
                description: '',
                board: [],
                elevationUp: '',
                elevationDown: '',
                duration: '',
                distance: '',
                driveDuration: '',
                driveDistance: '',
                sameAccommodation: false,
                hotelCategory: '',
                hotelCategoryCustom: '',
                accommodationData: null,
            },
        }),
        validations: {
            trip: {
                destinations: {

                },
                programs: {

                },
            },
        },
        computed: {
            ...mapGetters({
                isLoading: 'trips/isLoading',
                isUpdating: 'trips/isUpdating',
                tripStore: 'trips/trip',
                variantData: 'trips/variantData',
                activeVariant: 'trips/activeVariant',
            }),
            tripData() {
                if(this.variantData) {
                    return this.variantData;
                }

                return this.tripStore;
            },
            itineraryProperties() {
                const {
                    id,
                    duration,
                    destinations,
                    programs,
                } = this.tripData;

                return {
                    id,
                    duration,
                    destinations,
                    programs,
                };
            },
            geoJsonSource() {
                return {
                    type: 'geojson',
                    data: {
                        type: 'Feature',
                        geometry: {
                            type: 'LineString',
                            coordinates: this.trip.destinations.map(destination => {return destination.center;}),
                        },
                    },
                };
            },
        },
        watch: {
            selectedLocation(val) {
                if(val) {
                    this.trip.destinations.push(val);
                    this.$v.trip.destinations.$touch();

                    this.fitMapBounds();

                    this.selectedLocation = null;
                }
            },
            mapSearch(val) {
                if(val && val !== this.selectedLocation && val.length > 2) {
                    this.searchResultLoading = true;
                    axios.get(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURI(val)}.json?access_token=${this.accessToken}`)
                    .then(result => {
                        this.searchResultLoading = false;
                        this.searchResult = result.data.features.map(item => {
                            return {
                                value: {
                                    center: item.center,
                                    text: item.place_name,
                                },
                                label: item.place_name,
                            };
                        });
                    });
                }
            },
            watch: {
                activeVariant() {
                    this.init();
                },
            },
        },
        methods: {
            ...mapActions({
                save: `trips/${TripAction.UPDATE}`,
                setSnackbar: `snackbar/${SnackBarAction.SET}`,
            }),
            init() {
                this.trip = this.itineraryProperties;
                if(!this.trip.destinations) {
                    this.trip.destinations = [];
                } else {
                    this.trip.destinations = this.trip.destinations.map(destination => {
                        return {
                            center: [
                                destination.lng,
                                destination.lat,
                            ],
                            text: destination.text,
                        }
                    });
                }
                if(!this.trip.programs) {
                    this.trip.programs = [];
                }
                // no programs yet, create empty entries
                if(this.trip.programs.length === 0) {
                    for(let i = 1; i <= this.trip.duration; i++) {
                        let day = Object.assign({}, this.defaultDay);
                        day.dayFrom = i;
                        day.dayTo = i;
                        this.trip.programs.push(day);
                        this.amountOfDays.push(1);
                    }
                } else {
                    for(let i = 0, l = this.trip.programs.length; i < l; i++) {
                        // Check for accommodation data and set default if not available
                        if(
                            !this.trip.programs[i].accommodationData ||
                            (
                                Array.isArray(this.trip.programs[i].accommodationData) &&
                                this.trip.programs[i].accommodationData.length === 0
                            )
                        ) {
                            this.trip.programs[i].accommodationData = {
                                name: '',
                                street: '',
                                zip: '',
                                city: '',
                                country: '',
                            };
                        }

                        // Set default Day to
                        if(!this.trip.programs[i].dayTo) {
                            this.trip.programs[i].dayTo = this.trip.programs[i].dayFrom;
                        }

                        // Update amount of days
                        if(this.trip.programs[i].dayTo && this.trip.programs[i].dayFrom !== this.trip.programs[i].dayTo) {
                            this.amountOfDays.push(this.trip.programs[i].dayTo - this.trip.programs[i].dayFrom);
                        } else {
                            this.amountOfDays.push(1);
                        }
                    }
                }
            },
            validateTrip(onSuccess, onError){
                if(!this.$v.trip.$invalid) {
                    onSuccess(this.trip, this.init);
                }else{
                    onError();
                }
            },
            onMapLoaded(event) {
                this.map = event.map;

                this.fitMapBounds();
            },
            setDestinationPosition(component, key) {
                this.trip.destinations[key].center[0] = component.marker.getLngLat().lng;
                this.trip.destinations[key].center[1] = component.marker.getLngLat().lat;

                this.map.getSource('geoLocations').setData({
                    type: 'Feature',
                    geometry: {
                        type: 'LineString',
                        coordinates: this.trip.destinations.map(destination => {return destination.center;}),
                    },
                });
            },
            fitMapBounds() {
                if(this.trip.destinations.length > 0) {
                    let bounds = this.trip.destinations.reduce((bounds, coord) => {
                        return bounds.extend(coord.center);
                    }, new this.mapbox.LngLatBounds(this.trip.destinations[0].center, this.trip.destinations[0].center));

                    this.map.fitBounds(bounds, {
                        maxZoom: 13,
                        padding: 50,
                        linear: true,
                    });
                }
            },
            scrollTo(ref) {
                const ele = this.$refs[ref];

                ele[0].scrollIntoView();
            },
            updateDays(index, amountOfDays) {
                const shift = amountOfDays - 1;
                // update end for indexed day
                this.trip.programs[index].dayTo = this.trip.programs[index].dayFrom + shift;

                // shift remaining days
                for(let i = index + 1, l = this.trip.programs.length; i < l; i++) {
                    this.trip.programs[i].dayFrom = this.trip.programs[i].dayFrom + shift;
                    this.trip.programs[i].dayTo = this.trip.programs[i].dayTo + shift;
                }
            },
            updateAllDays() {
                if(this.trip.programs.length === 0) {
                    return;
                }

                // Update first day
                let diff = 0;
                if(this.trip.programs[0].dayTo) {
                    diff = this.trip.programs[0].dayTo - this.trip.programs[0].dayFrom;
                }
                this.trip.programs[0].dayFrom = 1;
                this.trip.programs[0].dayTo = this.trip.programs[0].dayFrom + diff;

                let previousEnd = this.trip.programs[0].dayTo;
                for(let i = 1, l = this.trip.programs.length; i < l; i++) {
                    let start = this.trip.programs[i].dayFrom;
                    let end = this.trip.programs[i].dayTo;
                    let diff = 0;
                    if(end) {
                        diff = end - start;
                    }
                    this.trip.programs[i].dayFrom = previousEnd + 1;
                    this.trip.programs[i].dayTo = this.trip.programs[i].dayFrom + diff;
                    previousEnd = this.trip.programs[i].dayTo;
                }
            },
            moveDay(index, direction) {
                this.trip.programs.splice((index + direction), 0, this.trip.programs.splice(index, 1)[0]);
                this.updateAllDays();
            },
            addDay() {
                let day = Object.assign({}, this.defaultDay);


                let start = 1;
                if(this.trip.programs.length > 0) {
                    start = this.trip.programs[this.trip.programs.length - 1].dayTo + 1;
                    const accommodationIndex = this.trip.programs.findIndex(program => {
                        return program.sameAccommodation;
                    });
                    day.accommodationData = Object.assign({}, this.trip.programs[accommodationIndex].accommodationData);
                }else{
                    day.accommodationData = Object.assign({}, this.defaultAccommodationData);
                }
                day.dayFrom = start;
                day.dayTo = start;
                this.trip.programs.push(day);
                this.amountOfDays.push(1);
            },
            deleteDay(index) {
                this.deleteIndex = index;
                this.dialogDelete = true;
            },
            closeDeleteDialog() {
                this.deleteIndex = -1;
                this.dialogDelete = false;
            },
            confirmDelete() {
                this.trip.programs.splice(this.deleteIndex, 1);
                this.amountOfDays.splice(this.deleteIndex, 1);
                this.updateAllDays();
                this.closeDeleteDialog();
            },
            dayToString(day) {
                let value = this.$t('day.day') + ` ${day.dayFrom}`;

                if (day.dayTo && day.dayTo !== day.dayFrom) {
                    value = value + ` - ${day.dayTo}`;
                }

                return value;
            }
        },
        beforeRouteLeave(to, from, next) {
            if(this.$v.$anyDirty) {
                let response = confirm('You have pending changes, unsaved changes will be lost');
                next(response);
            } else {
                next();
            }
        },
    }
</script>
